import { Add, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useFetch } from '../../../../hooks/useFecth';
import { useSearchTextInput } from '../../../../hooks/useSearchTextInput';
import server from '../../../../services/server';
import useErrors from '../../../../hooks/useErrors';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import ClubTabLayout from '../../Clubs/components/ClubTabLayout';

const roles = [
    { label: 'Proprietário', value: 'owner' },
    { label: 'Administrador', value: 'admin' },
    { label: 'Gerente', value: 'manager' },
    { label: 'Atendente', value: 'user' },
];

const userSchema = yup.object().shape({
    userName: yup.string().trim().email('Informe um e-mail válido').required('Informe o e-mail'),
    password: yup.string().test('min', 'Mínimo de 6 caracteres', value => !value || value.length >= 6),
    role: yup.string().required(),
});

function UserDialog({ advertiserId, open, onClose, assignment, onSuccess }) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: userSchema,
        initialValues: {
            userName: assignment?.user?.userName || '',
            password: '',
            role: assignment?.role || 'user',
            status: typeof assignment?.status === 'boolean' ? assignment.status : true,
            isEditing: Boolean(assignment),
        },
        onSubmit: async values => {
            try {
                const payload = {
                    role: values.role,
                    status: values.status,
                    password: values.password || undefined,
                    userName: values.userName.trim().toLowerCase(),
                };
                if (assignment?.id) {
                    await server.put(`advertiser/${advertiserId}/users/${assignment.id}`, payload);
                    enqueueSnackbar('Usuário atualizado.');
                } else {
                    await server.post(`advertiser/${advertiserId}/users`, payload);
                    enqueueSnackbar('Usuário adicionado.');
                }
                await onSuccess();
                onClose();
            } catch (error) {
                setErrors(error);
            }
        },
    });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{assignment ? 'Editar usuário' : 'Novo usuário'}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} pt={1}>
                    <TextField
                        label="E-mail"
                        name="userName"
                        type="email"
                        value={formik.values.userName}
                        onChange={formik.handleChange}
                        fullWidth
                        error={formik.touched.userName && Boolean(formik.errors.userName)}
                        helperText={formik.touched.userName && formik.errors.userName}
                    />
                    <TextField
                        label="Senha"
                        name="password"
                        type="password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        fullWidth
                        helperText={
                            assignment
                                ? 'Preencha apenas se deseja redefinir a senha.'
                                : 'Obrigatória se o e-mail ainda não tiver conta. Se a conta já existir, ela só ganha o vínculo.'
                        }
                        error={formik.touched.password && Boolean(formik.errors.password)}
                    />
                    <TextField select label="Cargo" name="role" value={formik.values.role} onChange={formik.handleChange} fullWidth>
                        {roles.map(role => (
                            <MenuItem key={role.value} value={role.value}>
                                {role.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    {assignment ? (
                        <FormControlLabel
                            control={<Switch name="status" checked={formik.values.status} onChange={formik.handleChange} />}
                            label="Ativo neste anunciante"
                        />
                    ) : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={formik.handleSubmit} variant="contained" disabled={formik.isSubmitting}>
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function ListAdvertiserUsers({ advertiserId }) {
    const { search, input, handleChange, handleReset } = useSearchTextInput();
    const { setErrors, enqueueSnackbar } = useErrors();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
    const [page, setPage] = useState(0);
    const perPage = 10;

    const params = useMemo(
        () => ({
            params: {
                page,
                perPage,
                search: search || undefined,
            },
        }),
        [page, perPage, search],
    );

    const { data: listResponse, mutate, isFetching } = useFetch(advertiserId ? `advertiser/${advertiserId}/users` : null, params);
    const users = listResponse?.data ?? [];
    const total = listResponse?.total ?? 0;
    const pages = listResponse?.pages ?? 1;

    useEffect(() => {
        setPage(0);
    }, [search]);

    const closeDialog = () => {
        setDialogOpen(false);
        setEditing(null);
    };

    const handleDelete = async () => {
        if (!deleteModal.user?.id) return;
        try {
            await server.delete(`advertiser/${advertiserId}/users/${deleteModal.user.id}`);
            enqueueSnackbar('Usuário desvinculado.');
            await mutate();
            setDeleteModal({ open: false, user: null });
        } catch (error) {
            setErrors(error);
        }
    };

    return (
        <>
            <ClubTabLayout
                actions={
                    <>
                        <TextField
                            size="small"
                            label="Buscar por e-mail"
                            value={input}
                            onChange={event => handleChange(event.target.value)}
                            sx={{ mr: 'auto', minWidth: { xs: '100%', sm: 260 } }}
                            InputProps={{
                                endAdornment: input ? (
                                    <Button size="small" onClick={handleReset}>
                                        Limpar
                                    </Button>
                                ) : null,
                            }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => {
                                setEditing(null);
                                setDialogOpen(true);
                            }}
                        >
                            Novo usuário
                        </Button>
                    </>
                }
            >
                <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>E-mail</TableCell>
                        <TableCell>Cargo</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Criado em</TableCell>
                        <TableCell align="right" width={100}>
                            Ações
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {users.map(item => (
                        <TableRow key={item.id} hover>
                            <TableCell>{item.user?.userName}</TableCell>
                            <TableCell>{roles.find(role => role.value === item.role)?.label ?? item.role}</TableCell>
                            <TableCell>{item.status ? 'Ativo' : 'Inativo'}</TableCell>
                            <TableCell align="right">
                                {item.user?.createdAt ? new Date(item.user.createdAt).toLocaleDateString('pt-BR') : '--'}
                            </TableCell>
                            <TableCell align="right">
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setEditing(item);
                                        setDialogOpen(true);
                                    }}
                                    aria-label="Editar"
                                >
                                    <Edit fontSize="small" />
                                </IconButton>
                                {item.role !== 'owner' ? (
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => setDeleteModal({ open: true, user: item })}
                                        aria-label="Remover"
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                ) : null}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {users.length === 0 && !isFetching && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }} textAlign="center">
                    Nenhum usuário cadastrado.
                </Typography>
            )}
            {pages > 1 && (
                <Box display="flex" justifyContent="center" alignItems="center" gap={1} sx={{ px: 2, py: 1.5 }}>
                    <Button variant="outlined" size="small" disabled={page <= 0} onClick={() => setPage(current => Math.max(0, current - 1))}>
                        Anterior
                    </Button>
                    <Typography variant="body2">
                        Página {page + 1} de {pages} ({total} usuários)
                    </Typography>
                    <Button variant="outlined" size="small" disabled={page >= pages - 1} onClick={() => setPage(current => current + 1)}>
                        Próxima
                    </Button>
                </Box>
            )}
            </ClubTabLayout>
            <UserDialog
                advertiserId={advertiserId}
                open={dialogOpen}
                onClose={closeDialog}
                assignment={editing}
                onSuccess={() => mutate()}
            />
            <ConfirmationModal
                open={deleteModal.open}
                title="Remover usuário"
                message="O vínculo com este anunciante será apagado. A conta em si permanece."
                onConfirm={handleDelete}
                onClose={() => setDeleteModal({ open: false, user: null })}
            />
        </>
    );
}
